import * as React from "react";
import Link from "next/link";
import {
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import {
  Button,
} from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type Crumb = { label: string; href?: string };

type PageHeaderProps = {
  eyebrow: string;
  title: string;
  description?: string;
  breadcrumbs?: Crumb[];
  /** Rendered inside the header help popover. Never inline next to the <h1>. */
  help?: React.ReactNode;
  /** Ghost/outline actions. */
  secondaryActions?: React.ReactNode;
  /** Exactly one filled orange action per view. */
  primaryAction?: React.ReactNode;
};

export function PageHeader({
  eyebrow,
  title,
  description,
  breadcrumbs,
  help,
  secondaryActions,
  primaryAction,
}: PageHeaderProps) {
  return (
    <header className="flex flex-col gap-3">
      {breadcrumbs?.length ? (
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-[13px] text-muted-foreground"
        >
          {breadcrumbs.map((c, i) => (
            <React.Fragment key={c.label}>
              {i > 0 && (
                <ChevronRight className="size-3.5 shrink-0 opacity-60" />
              )}
              {c.href ? (
                <Link href={c.href} className="hover:underline">
                  {c.label}
                </Link>
              ) : (
                <span className="text-foreground">{c.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      ) : null}

      <div className="flex items-start gap-6 max-md:flex-col max-md:gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {eyebrow}
          </p>
          <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.015em] text-foreground">
            {title}
          </h1>
          {description ? (
            <p className="text-sm text-muted-foreground text-pretty">
              {description}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-[22px] max-md:w-full max-md:pt-0">
          {help ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Help"
                  className="size-9"
                >
                  <HelpCircle className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-4">
                {help}
              </PopoverContent>
            </Popover>
          ) : null}
          {secondaryActions}
          {primaryAction}
        </div>
      </div>
    </header>
  );
}
